import { Link } from "react-router-dom";
import { usePageData } from "@/hooks/usePageData";
import { useSEO } from "@/hooks/useSEO";
import { Target, Clock, Shield, BarChart3, Lightbulb, Handshake, Scale, Lock } from "lucide-react";

const iconMap = { Lock, Scale, Shield, Clock, Target, Lightbulb, BarChart3, Handshake };
const getIcon = (name) => iconMap[name] || Target;

export default function AboutPage() {
  const { sections } = usePageData("about");
  const hero = sections.hero || {};
  const story = sections.our_story || {};
  const philosophy = sections.philosophy || {};
  const howWeWork = sections.how_we_work || {};
  const values = sections.values || {};
  const cta = sections.cta || {};
  const pageTitle = hero.meta_title || "About Us | Equaliser Legal LLP";
  const pageDesc = hero.meta_description || "Founded by a former senior executive, Equaliser Legal LLP combines deep business experience with qualified legal practice across India, the Middle East, and the UK.";
  useSEO(pageTitle, pageDesc);

  return (
    <div data-testid="about-page">
      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{hero.subtitle || "About Us"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight tracking-tight max-w-3xl">{hero.title || "Where business experience meets legal practice."}</h1>
        </div>
      </section>

      <section data-testid="our-story" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{story.subtitle || "Our Story"}</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-8">{story.title || "Born from industry leadership and legal practice."}</h2>
              <div className="text-slate-600 text-base leading-relaxed space-y-5" dangerouslySetInnerHTML={{ __html: story.body || "<p>Equaliser Legal LLP was founded by a former senior executive and Managing Director of a leading global multinational corporation.</p>" }} />
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-[2px]">
                <img src={story.image_url || "https://images.pexels.com/photos/9786294/pexels-photo-9786294.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt="Legal advisory" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="philosophy" className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{philosophy.subtitle || "Our Philosophy"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{philosophy.title || "Clarity. Speed. Practicality."}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(philosophy.items || [
              { title: "Clarity", description: "Every piece of advice is expressed in plain language.", icon: "Lightbulb" },
              { title: "Speed", description: "Business moves fast. We deliver responsive, timely counsel.", icon: "Clock" },
              { title: "Risk Awareness", description: "We identify and manage legal risk proportionately.", icon: "BarChart3" },
              { title: "Practicality", description: "Our advice is designed to be implemented.", icon: "Handshake" }
            ]).map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} data-testid={`philosophy-item-${i}`}>
                  <Icon size={24} className="text-[#C5A059] mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-3">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="how-we-work" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{howWeWork.subtitle || "How We Work"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{howWeWork.title || "Flexible engagement, consistent quality."}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(howWeWork.items || [
              { num: "01", title: "Retainer Support", description: "Monthly retainer arrangements for ongoing contracts review, compliance queries, and day-to-day legal support." },
              { num: "02", title: "Project-Based Work", description: "Scoped engagements for specific matters: contract drafting, compliance audits, policy development, or transaction support." },
              { num: "03", title: "Disputes Oversight", description: "Strategic oversight and coordination for litigation, arbitration, and dispute resolution across India, the Middle East, and the UK." }
            ]).map((item, i) => (
              <div key={i} className="border border-slate-200 p-8 md:p-10 hover:border-[#0A1628] transition-colors duration-300">
                <span className="text-[#C5A059] text-xs tracking-widest font-medium">{item.num}</span>
                <h3 className="font-heading text-xl text-[#0A1628] font-medium mt-4 mb-4">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="values" className="bg-[#0A1628] noise-overlay relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{values.subtitle || "Our Values"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-white font-medium tracking-tight mb-16">{values.title || "Principles that guide our practice."}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {(values.items || [
              { title: "Integrity", description: "We are straightforward and transparent.", icon: "Lock" },
              { title: "Independence", description: "Objective counsel, free from conflicts.", icon: "Scale" },
              { title: "Confidentiality", description: "Client information held in strict confidence.", icon: "Shield" },
              { title: "Responsiveness", description: "Timely advice is effective advice.", icon: "Clock" },
              { title: "Proportionality", description: "Right-sized solutions.", icon: "Target" }
            ]).map((v, i) => {
              const Icon = getIcon(v.icon);
              return (
                <div key={i} data-testid={`value-item-${i}`}>
                  <Icon size={24} className="text-[#C5A059] mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-white font-medium mb-2">{v.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-6">{cta.title || "Ready to work with a team that understands your business?"}</h2>
          <Link to={cta.cta_link || "/contact"} data-testid="about-cta" className="inline-flex items-center justify-center bg-[#0A1628] text-white px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#1a2c4e] transition-colors duration-300 rounded-[2px]">{cta.cta_text || "Get in touch"}</Link>
        </div>
      </section>
    </div>
  );
}
